import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'

import type { PublicBranding } from '../../api/types.gen'
import { ApiException, api } from '../../api/client'
import { applyTheme } from '../../theme'
import { Banner, Button, Field } from '../../ui'
import { useSession } from './useSession'

/** Lo que devuelve `GET /tenant/branding`: sólo lo que ya es público. */

export function LoginPage() {
  const { login, status } = useSession()
  // Tras el alta se llega desde el host de la aplicación con el correo en la
  // URL. No es una sesión: el registro no emite token porque la cookie de
  // refresco quedaría en el dominio equivocado. Pero al menos no obligamos a
  // teclear el correo otra vez treinta segundos después de escribirlo.
  const [params] = useSearchParams()
  const justRegistered = params.get('nuevo') === '1'
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (status === 'authenticated') return <Navigate to="/projects" replace />

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      await login(email, password)
    } catch (caught) {
      setError(
        caught instanceof ApiException && caught.isValidation
          ? 'Email o contraseña incorrectos.'
          : 'No se pudo conectar. Revisa tu conexión.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center px-4 pb-safe pt-safe">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex w-full max-w-form flex-col gap-5"
      >
        <StudioMark />

        <h1 className="text-title font-semibold text-ink">
          {justRegistered ? 'Tu cuenta está lista' : 'Entrar'}
        </h1>

        {justRegistered && (
          <Banner tone="positive">Entra con tu contraseña para añadir tu tarjeta y empezar.</Banner>
        )}

        <div className="flex flex-col gap-4">
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
            // inputMode + autoCapitalize evitan el teclado equivocado y la
            // mayúscula automática de iOS, que rompe el email al escribirlo.
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
          />

          <Field
            label="Contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            // El error va en el segundo campo y no suelto: describe la pareja
            // email/contraseña, y ahí es donde está el cursor cuando falla.
            error={error}
          />
        </div>

        <Button type="submit" size="lg" block busy={submitting && 'Entrando…'}>
          Entrar
        </Button>
      </form>
    </div>
  )
}

/**
 * Nombre y logo del estudio al que se está entrando.
 *
 * No es decoración. Cada cuenta vive en su propio dominio y esta pantalla era
 * idéntica en todos: entrar con el email de otro estudio devuelve «credenciales
 * inválidas» —correcto, porque el backend no distingue entre email inexistente
 * y contraseña mala para que nadie pueda enumerar cuentas— y sin nada en
 * pantalla que diga dónde estás, ese mensaje es indescifrable.
 *
 * Y es la pantalla donde el fotógrafo entra cada día, en su dominio: verla con
 * nuestro azul en un producto que vende «con tu nombre» es justo donde peor
 * sienta.
 *
 * Falla en silencio a propósito. Si la marca no carga, se entra igual; bloquear
 * el login porque no llegó un logo sería absurdo.
 */
function StudioMark() {
  const applied = useRef(false)

  const { data } = useQuery({
    queryKey: ['tenant', 'branding'],
    queryFn: () => api.get<{ data: PublicBranding }>('/tenant/branding'),
    retry: false,
    // La marca de un estudio cambia como mucho una vez al año.
    staleTime: 5 * 60_000,
  })

  const studio = data?.data

  useEffect(() => {
    // `applied` evita repintar en cada render de React 19 en desarrollo, que
    // monta dos veces: sin la guarda, el tema se escribe una vez por montaje.
    if (studio === undefined || applied.current) return

    applied.current = true
    applyTheme(studio.branding)
    document.title = `Entrar · ${studio.name}`
  }, [studio])

  if (studio === undefined) {
    // Hueco reservado con la altura final: sin él, el formulario da un salto
    // hacia abajo cuando llega el nombre, justo mientras se escribe el email.
    return <div className="h-9" aria-hidden />
  }

  return (
    <div className="flex h-9 items-center gap-2.5">
      {studio.branding.logo_url !== undefined ? (
        <img src={studio.branding.logo_url} alt={studio.name} className="h-9 object-contain" />
      ) : (
        <>
          <span
            aria-hidden
            className="grid h-9 w-9 place-items-center rounded-control bg-accent text-subhead font-semibold text-accent-contrast"
          >
            {studio.name.trim().charAt(0).toUpperCase()}
          </span>
          <span className="truncate text-subhead font-semibold text-ink">{studio.name}</span>
        </>
      )}
    </div>
  )
}
